import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getOrderTracking, acceptPaymentAndPay } from '../services/api';
import '../styles/OrderTracking.css';

const OrderTracking = () => {
  const { t } = useTranslation();
  const { paymentId } = useParams();
  const navigate = useNavigate();
  
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);

  // Rafraîchir les données toutes les 30 secondes
  useEffect(() => {
    fetchPaymentDetails();
    
    const interval = setInterval(() => {
      if (payment?.status === 'paid') {
        fetchPaymentDetails();
      }
    }, 30000); // 30 secondes

    return () => clearInterval(interval);
  }, [paymentId]);

  const fetchPaymentDetails = async () => {
    try {
      const result = await getOrderTracking(paymentId);
      
      if (result.success) {
        setPayment(result.payment);
        
        if (result.payment.status === 'paid' && result.payment.status !== payment?.status) {
          // Première fois qu'on voit que c'est payé
          console.log('Paiement confirmé !');
        }
      } else {
        setError(result.error || t('checkout.error.notFound'));
      }
    } catch (err) {
      setError(t('checkout.error.notFound'));
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!accepted) {
      alert(t('checkout.button.accept'));
      return;
    }

    setProcessingPayment(true);
    setError('');

    try {
      const result = await acceptPaymentAndPay(paymentId);

      if (result.success) {
        window.location.href = result.stripeUrl;
      } else {
        setError(result.error || t('checkout.error.notFound'));
      }
    } catch (err) {
      setError(t('checkout.error.notFound'));
    } finally {
      setProcessingPayment(false);
    }
  };

  const formatCurrency = (amount, currency = 'eur') => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount);
  };

  const getStepIcon = (index) => {
    const icons = ['💳', '🎨', '💻', '✅', '🚀'];
    return icons[index] || '📦';
  };

  const getStepStatus = (step) => {
    if (step.status === 'completed') return 'completed';
    if (step.status === 'in_progress') return 'in-progress';
    return 'pending';
  };

  if (loading) {
    return (
      <div className="order-tracking-page">
        <div className="tracking-loading">
          <div className="spinner"></div>
          <p>{t('checkout.loading')}</p>
        </div>
      </div>
    );
  }

  if (error && !payment) {
    return (
      <div className="order-tracking-page">
        <div className="tracking-error">
          <span className="error-icon">❌</span>
          <h2>{t('checkout.error.title')}</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/')} className="home-btn">
            {t('checkout.backHome')}
          </button>
        </div>
      </div>
    );
  }

  // Si pas encore payé, afficher le checkout
  if (payment.status === 'pending') {
    return (
      <div className="order-tracking-page checkout-mode">
        <div className="tracking-container">
          <div className="tracking-header">
            <h1>💳 {t('checkout.title')}</h1>
            <p>{t('checkout.subtitle')}</p>
          </div>

          <div className="tracking-card">
            <div className="card-section">
              <h2>📋 {t('checkout.details.title')}</h2>
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">{t('checkout.details.client')}</span>
                  <span className="detail-value">{payment.clientName}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">{t('checkout.details.project')}</span>
                  <span className="detail-value">{payment.projectName}</span>
                </div>
                {payment.description && (
                  <div className="detail-item full-width">
                    <span className="detail-label">{t('checkout.details.description')}</span>
                    <p className="detail-description">{payment.description}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="card-section amount-section">
              <div className="amount-display">
                <span className="amount-label">{t('checkout.amount.total')}</span>
                <span className="amount-value">
                  {formatCurrency(payment.amount, payment.currency)}
                </span>
              </div>
              <p className="amount-note">{t('checkout.amount.vat')}</p>
            </div>

            <div className="card-section">
              <h2>💳 {t('checkout.payment.title')}</h2>
              <div className="payment-method">
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg" 
                  alt="Stripe"
                  className="stripe-logo"
                />
                <p>{t('checkout.payment.secured')} <strong>Stripe</strong></p>
                <div className="payment-badges">
                  <span className="badge">🔒 {t('checkout.payment.badges.ssl')}</span>
                  <span className="badge">💳 {t('checkout.payment.badges.card')}</span>
                  <span className="badge">🏦 {t('checkout.payment.badges.transfer')}</span>
                </div>
              </div>
            </div>

            <div className="card-section cgv-section">
              <h2>📜 {t('checkout.cgv.title')}</h2>
              <div className="cgv-box">
                <ul>
                  <li>✓ {t('checkout.cgv.items.secure')}</li>
                  <li>✓ {t('checkout.cgv.items.invoice')}</li>
                  <li>✓ {t('checkout.cgv.items.start')}</li>
                  <li>✓ {t('checkout.cgv.items.delay')}</li>
                  <li>✓ {t('checkout.cgv.items.support')}</li>
                  <li>✓ {t('checkout.cgv.items.revisions')}</li>
                </ul>
              </div>

              <label className="checkbox-container">
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                />
                <span className="checkbox-text">
                  {t('checkout.cgv.accept')}{' '}
                  <a href="/cgv" target="_blank" rel="noopener noreferrer">
                    {t('checkout.cgv.link')}
                  </a>
                  {' '}{t('checkout.cgv.nonRefundable')}
                </span>
              </label>
            </div>

            {error && (
              <div className="error-message">{error}</div>
            )}

            <div className="checkout-actions">
              <button
                onClick={handlePayment}
                disabled={!accepted || processingPayment}
                className="pay-btn"
              >
                {processingPayment ? (
                  <>
                    <span className="btn-spinner"></span>
                    {t('checkout.button.processing')}
                  </>
                ) : (
                  <>🔒 {t('checkout.button.pay')}</>
                )}
              </button>
              
              <p className="security-note">
                🔒 {t('checkout.security')}
              </p>
            </div>
          </div>

          <div className="tracking-footer">
            <p>
              {t('checkout.footer.question')} <a href="mailto:ibra.developpeur@gmail.com">ibra.developpeur@gmail.com</a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Si payé, afficher le suivi de progression
  return (
    <div className="order-tracking-page tracking-mode">
      <div className="tracking-container">
        <div className="tracking-header">
          <h1>📦 Suivi de commande</h1>
          <p>Suivez l'avancement de votre projet en temps réel</p>
        </div>

        <div className="tracking-card">
          {/* Résumé de la commande */}
          <div className="order-summary">
            <div className="summary-header">
              <h2>{payment.projectName}</h2>
              <span className="status-badge status-paid">✓ Payé</span>
            </div>
            <div className="summary-details">
              <div className="summary-item">
                <span className="summary-label">Client</span>
                <span className="summary-value">{payment.clientName}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Montant</span>
                <span className="summary-value">{formatCurrency(payment.amount, payment.currency)}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Référence</span>
                <span className="summary-value">#{payment._id?.slice(-8).toUpperCase()}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Date de paiement</span>
                <span className="summary-value">
                  {new Date(payment.paidAt || payment.createdAt).toLocaleDateString('fr-FR')}
                </span>
              </div>
            </div>
          </div>

          {/* Progression */}
          <div className="progress-section">
            <h3>Progression du projet</h3>
            <div className="progress-steps">
              {payment.progress?.steps?.map((step, index) => (
                <div key={index} className={`progress-step ${getStepStatus(step)}`}>
                  <div className="step-icon">{getStepIcon(index)}</div>
                  <div className="step-content">
                    <div className="step-header">
                      <h4>{step.name}</h4>
                      {step.status === 'completed' && step.completedAt && (
                        <span className="step-date">
                          ✓ {new Date(step.completedAt).toLocaleDateString('fr-FR')}
                        </span>
                      )}
                      {step.status === 'in_progress' && (
                        <span className="step-status-badge in-progress">En cours</span>
                      )}
                    </div>
                    {step.notes && (
                      <p className="step-notes">{step.notes}</p>
                    )}
                  </div>
                  {index < payment.progress.steps.length - 1 && (
                    <div className={`step-connector ${step.status === 'completed' ? 'completed' : ''}`}></div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Mises à jour */}
          {payment.updates && payment.updates.length > 0 && (
            <div className="updates-section">
              <h3>📢 Dernières nouvelles</h3>
              <div className="updates-list">
                {payment.updates.slice().reverse().map((update, index) => (
                  <div key={index} className="update-item">
                    <div className="update-header">
                      <h4>{update.title}</h4>
                      <span className="update-date">
                        {new Date(update.createdAt).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                    <p className="update-message">{update.message}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Contact */}
          <div className="contact-section">
            <p>
              Des questions ? Contactez-moi à{' '}
              <a href="mailto:ibra.developpeur@gmail.com">ibra.developpeur@gmail.com</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderTracking;